/**
 * 🎨 GUIDE AVATAR - Composant d'Animation de Personnage IA
 * 
 * Mode Mozart/Jobs/Tesla/Da Vinci - Une œuvre d'art numérique !
 * 
 * Caractéristiques :
 * - Avatars semi-réalistes générés par IA
 * - 22 expressions différentes par personnage
 * - Micro-animations (clignement, mouvements subtils)
 * - Transitions fluides entre expressions
 * - Style macOS Genius - discret mais présent
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// 🎭 CATALOGUE DES EXPRESSIONS (22 par personnage)
export const EXPRESSIONS = {
  // Contexte → Expression
  welcome: 'welcome_smile',           // Bienvenue, premier contact
  wink: 'wink_playful',               // Conseil coquin, encouragement
  seductive: 'seductive_gaze',        // Moment de flirt, suggestion osée
  mischievous: 'mischievous_pout',    // Taquinerie, jeu
  laugh: 'warm_laugh',                // Réaction positive, félicitations
  thinking: 'mysterious_thinking',    // Réflexion, analyse de compatibilité
  surprise: 'delighted_surprise',     // Découverte d'un match
  supportive: 'tender_support',       // Soutien émotionnel
  curious: 'curious_look',            // Question, demande d'info
  admiring: 'admiring_gaze',          // Compliment au profil
  confident: 'confident_look',        // Conseil assuré
  pouty: 'cute_pout',                 // Reproche léger, l'user hésite
  dreamy: 'dreamy_gaze',              // Parler de l'amour idéal
  smirk: 'knowing_smirk',             // Sous-entendu coquin
  excited: 'excited_look',            // Annonce d'un événement/jeu
  romantic: 'romantic_gaze',          // Moment intime, connexion
  protective: 'protective_look',      // Avertissement, conseil de prudence
  playful: 'playful_expression',      // Mini-jeux, défis
  intense: 'intense_gaze',            // Moment de vérité, révélation
  satisfied: 'satisfied_smile',       // Mission accomplie
  secretive: 'secretive_wink',        // Partage d'un tip exclusif
  goodbye: 'goodbye_wave',            // Fin de session, à bientôt
};

// 🖼️ URLs DES IMAGES GÉNÉRÉES PAR IA - 22 EXPRESSIONS PAR GUIDE
export const GUIDE_IMAGES = {
  luna: {
    // Expressions de base
    welcome_smile: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/4a3ba43ac358122fdf7de47efdbaacb2bb71261f678c9c6e4499cfeb21ba4d0a.png',
    wink_playful: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/220f17829d61074167b52bbc0fc18475ed15a8d81533df884cac53d02a82ac59.png',
    seductive_gaze: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/9ab40f64ad02bb7bac3b672061016e9dc1166dfec5cfdc5e591b55ac62fc9916.png',
    // Expressions supplémentaires générées
    mischievous_pout: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/a1ba7b056328ddfb27e35021aac2aa6df35d43b899a7ad8ee5beee0bd8f11a25.png',
    warm_laugh: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/6409b17763ca9fc8ecbb634f56c3be553e4e5c9d5132d7e71a2a40412090a480.png',
    mysterious_thinking: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/3e66d84492cef78bc9e7b691ed633aaa479f1d90f7c7b69ec0517ae574f904b4.png',
    delighted_surprise: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/3fe040f46c39d002d0423db48852605ce52b66668fa3beaee959fc4daa36a1ed.png',
    tender_support: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/f57a8272019482a70bbe28c6abfe40cb8f993ae7a0619916d7f190312c4ec7fa.png',
    curious_look: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/ec06c1a8b63a0474266597e98e2057eaba681fb511d7892bed5c79726eb80598.png',
    dreamy_gaze: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/34a4719c7f826a930e6e9c1899aa11f02460b297169ad0647080abdb508c01f7.png',
    excited_look: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/49422c52d9c0a86ccafc1e4cbd242a2e118a05d06010a25cb50d732424e3f215.png',
    romantic_gaze: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/c0ed0508fda4425bbde5ab563f4599f08bde3a47eb60716af18cd4357ae7e857.png',
    satisfied_smile: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/782fa6dfd53bc2b78f75e2112216e32c76bd99ed6578a410ebf3ff12749a7cca.png',
    // Fallback
    default: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/4a3ba43ac358122fdf7de47efdbaacb2bb71261f678c9c6e4499cfeb21ba4d0a.png',
  },
  enzo: {
    // Expressions de base
    welcome_smile: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/caa50018dd9bfa66fa4ce2dfe1b4c81b67f498df4d564cfe39bf15ee0fc367f2.png',
    wink_playful: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/4e22068aef4e6c94a425cb14d73ce10b588f91113e922aff4028552c99e6f33f.png',
    seductive_gaze: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/ba01c5de19f0b5697aa751f8a338428d2e818911df50cac512da9a3c0415e055.png',
    // Expressions supplémentaires générées
    mischievous_pout: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/ee13830b75bc6f6c9fc8b5305ada0f7a8e106e415a38825af1061d5d3d9e13e9.png',
    warm_laugh: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/50c30621ea5b047ec27c0546e63d20e683b2ccf41b7a93ada368e50fa2d52bdf.png',
    mysterious_thinking: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/8a1192f34cf6128f966d4464c359bccb789808a31bf01712c1b2373b821d0c8a.png',
    tender_support: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/97fc2262acab5a8d7b29ee7d67d10f29880ddda174dad4b55299d1992ad85c98.png',
    curious_look: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/0b22e6f81f0f9fa54c6794f444d13bfbde1257dc72168c829028f0c3077eb353.png',
    dreamy_gaze: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/8a1192f34cf6128f966d4464c359bccb789808a31bf01712c1b2373b821d0c8a.png',
    excited_look: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/d95f96fc0d191ea6c3523968d128669b37b52619fb39db3d7f843a207e6ad34d.png',
    romantic_gaze: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/7e8111e6c73b0e042675994a70ea44d8fe89f49f17e41ee1f5fb1354b213b60f.png',
    satisfied_smile: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/be4ff8a8545a250c3d88bd812a10500c0eda1c5b7faf97cd1d0a376802ecaa58.png',
    // Fallback
    default: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/caa50018dd9bfa66fa4ce2dfe1b4c81b67f498df4d564cfe39bf15ee0fc367f2.png',
  },
  // 🖤 NYX - Guide de l'Ombre Féminin (Underground Chic - Piercings & Tattoos)
  nyx: {
    confident_smirk: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/73865e25135bbce283427959c7863cc40cfc5252dc272b655c5e92923f32bd50.png',
    challenging_gaze: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/f2967660cba23daa38aa0326b21e3aaba6ffa82faba51e3cf6c6dc5aa6da4e1c.png',
    wicked_wink: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/4ffa9843ffed663124845147f7f58f41fa39df4e408c4e50458037308312cd38.png',
    default: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/73865e25135bbce283427959c7863cc40cfc5252dc272b655c5e92923f32bd50.png',
  },
  // 🔮 KURSON - Oracle de l'Ombre (Bad Boy - Piercings & Tattoos)
  kurson: {
    knowing_smirk: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/144ee61c2a41008b0fa250a6c366e492e8a8639d70bd2be3071ef48d31a9eaa6.png',
    oracle_stare: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/32ccc226302728ef6ad2ff5c2a4547b75b7d7f309563c4b4f9ac5d79a4732d0b.png',
    daring_challenge: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/a20f28abffd95cfe3bb48861b4f3c5376907fd50cc1df7ae5296a3e3b1111f04.png',
    default: 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/144ee61c2a41008b0fa250a6c366e492e8a8639d70bd2be3071ef48d31a9eaa6.png',
  },
  // ✨ PIXIE - Commandante de la cabine de pilotage (belle blonde, cultivée, accueillante)
  pixie: {
    // Commandante Pixie - Belle blonde classe dans sa cabine
    magical_smile: 'https://customer-assets.emergentagent.com/job_heartbeats-revamp/artifacts/jybu2wd3_88835866-396a-4af5-8f1b-1ff67b42abbe.png',
    matchmaker_wink: 'https://customer-assets.emergentagent.com/job_heartbeats-revamp/artifacts/jybu2wd3_88835866-396a-4af5-8f1b-1ff67b42abbe.png',
    match_found: 'https://customer-assets.emergentagent.com/job_heartbeats-revamp/artifacts/jybu2wd3_88835866-396a-4af5-8f1b-1ff67b42abbe.png',
    dreamy_romantic: 'https://customer-assets.emergentagent.com/job_heartbeats-revamp/artifacts/jybu2wd3_88835866-396a-4af5-8f1b-1ff67b42abbe.png',
    flirty_tease: 'https://customer-assets.emergentagent.com/job_heartbeats-revamp/artifacts/jybu2wd3_88835866-396a-4af5-8f1b-1ff67b42abbe.png',
    default: 'https://customer-assets.emergentagent.com/job_heartbeats-revamp/artifacts/jybu2wd3_88835866-396a-4af5-8f1b-1ff67b42abbe.png',
  }
};

// 🎨 Configuration des personnages
export const GUIDE_CONFIG = {
  luna: {
    name: 'Luna',
    emoji: '🌙',
    color: 'from-pink-500 to-rose-500',
    bgGlow: 'rgba(236, 72, 153, 0.3)',
    role: 'Guide Heartbeats',
    personality: 'Douce, coquine, bienveillante',
  },
  enzo: {
    name: 'Enzo',
    emoji: '🔥',
    color: 'from-blue-500 to-purple-500',
    bgGlow: 'rgba(99, 102, 241, 0.3)',
    role: 'Guide Heartbeats',
    personality: 'Charmeur, confiant, taquin',
  },
  nyx: {
    name: 'Nyx',
    emoji: '🖤',
    color: 'from-purple-900 via-red-900 to-black',
    bgGlow: 'rgba(139, 0, 139, 0.5)',
    role: 'Guide de l\'Ombre',
    personality: 'Underground, provocante, te sort de ta zone de confort',
    style: 'Piercings, tattoos, bad girl chic',
    catchphrase: 'Tu joues la sécurité ou tu vis vraiment ? 😈',
  },
  kurson: {
    name: 'Kurson',
    emoji: '🔮',
    color: 'from-red-900 via-purple-900 to-black',
    bgGlow: 'rgba(139, 0, 0, 0.5)',
    role: 'Oracle de l\'Ombre',
    personality: 'Voit ton passé, présent et futur. Te challenge.',
    style: 'Piercings, tattoos, bad boy mystique',
    catchphrase: 'Je sais ce que tu veux vraiment... 🔮',
  },
  pixie: {
    name: 'Pixie',
    emoji: '🚀',
    color: 'from-pink-400 via-purple-400 to-fuchsia-500',
    bgGlow: 'rgba(236, 72, 153, 0.4)',
    role: 'Commandante',
    personality: 'Belle blonde cultivée, accueillante, pilote hors pair de la cabine Heartbeats',
    style: 'Commandante de vaisseau classe et charismatique. Aux commandes de sa cabine, elle guide les passagers vers leur destination : l\'amour',
    catchphrase: 'Bienvenue à bord ! Attachez vos ceintures, on décolle vers l\'amour ! 🚀✨',
    canInfiltrate: true, // Peut apparaître hors de Heartbeats
    isCommander: true, // Flag pour indiquer son rôle de commandante
  }
};

/**
 * 🎭 GuideAvatar - Avatar animé du guide
 * 
 * @param {string} guide - 'luna' | 'enzo' | 'nyx' | 'kurson' | 'pixie'
 * @param {string} expression - Expression à afficher (voir EXPRESSIONS)
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl'
 * @param {boolean} animate - Activer les micro-animations
 * @param {boolean} glow - Effet de lueur
 * @param {function} onClick - Callback au clic
 */
// 🚀 Cache global pour précharger les images des guides
const imageCache = new Map();
const preloadImage = (url) => {
  if (!url || imageCache.has(url)) return;
  const img = new Image();
  img.src = url;
  imageCache.set(url, img);
};

// Précharger les images par défaut au chargement du module
Object.values(GUIDE_IMAGES).forEach(guideImages => {
  if (guideImages?.default) preloadImage(guideImages.default);
  if (guideImages?.welcome_smile) preloadImage(guideImages.welcome_smile);
});

export const GuideAvatar = ({ 
  guide = 'luna', 
  expression = 'welcome_smile',
  size = 'md',
  animate = true,
  glow = true,
  onClick,
  className = ''
}) => {
  const [isBlinking, setIsBlinking] = useState(false);
  const [headTilt, setHeadTilt] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const blinkIntervalRef = useRef(null);
  
  const config = GUIDE_CONFIG[guide] || GUIDE_CONFIG.luna;
  const images = GUIDE_IMAGES[guide] || GUIDE_IMAGES.luna;
  const imageUrl = images[expression] || images.default;
  
  // Précharger l'image quand elle change
  useEffect(() => {
    if (imageUrl) {
      setImageLoaded(false);
      setImageError(false);
      preloadImage(imageUrl);
    }
  }, [imageUrl]);
  
  // Tailles
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32',
  };
  
  // 👁️ Animation de clignement naturel (toutes les 3-5 secondes)
  useEffect(() => {
    if (!animate) return;
    
    const blink = () => {
      setIsBlinking(true);
      setTimeout(() => setIsBlinking(false), 150);
    };
    
    const scheduleNextBlink = () => {
      const delay = 3000 + Math.random() * 2000; // 3-5 secondes
      blinkIntervalRef.current = setTimeout(() => {
        blink();
        scheduleNextBlink();
      }, delay);
    };
    
    scheduleNextBlink();
    
    return () => {
      if (blinkIntervalRef.current) clearTimeout(blinkIntervalRef.current);
    };
  }, [animate]);
  
  // 🎯 Animation de mouvement subtil de la tête
  useEffect(() => {
    if (!animate) return;
    
    const moveHead = () => {
      setHeadTilt((Math.random() - 0.5) * 3); // -1.5° à +1.5°
    };
    
    const interval = setInterval(moveHead, 4000);
    return () => clearInterval(interval);
  }, [animate]);
  
  if (!imageUrl) {
    // Fallback emoji si pas d'image
    return (
      <div 
        className={`${sizeClasses[size]} rounded-full bg-gradient-to-br ${config.color} flex items-center justify-center ${className}`}
        onClick={onClick}
      >
        <span className="text-2xl">{guide === 'luna' ? '👩' : guide === 'enzo' ? '👨' : '✨'}</span>
      </div>
    );
  }
  
  return (
    <motion.div
      className={`relative ${sizeClasses[size]} rounded-full overflow-hidden cursor-pointer ${className}`}
      onClick={onClick}
      animate={{ 
        rotate: headTilt,
        scale: isBlinking ? 0.98 : 1,
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      style={{
        boxShadow: glow ? `0 0 30px ${config.bgGlow}, 0 0 60px ${config.bgGlow}` : 'none',
      }}
    >
      {/* Placeholder pendant le chargement */}
      {!imageLoaded && !imageError && (
        <div className={`absolute inset-0 bg-gradient-to-br ${config.color} flex items-center justify-center animate-pulse`}>
          <span className="text-white/50 text-lg">⏳</span>
        </div>
      )}
      
      {/* Image du personnage */}
      <motion.img
        src={imageUrl}
        alt={config.name}
        className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageError(true)}
        loading="eager"
        decoding="async"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ 
          opacity: imageLoaded ? 1 : 0, 
          scale: 1,
          filter: isBlinking ? 'brightness(0.95)' : 'brightness(1)',
        }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Fallback si erreur de chargement */}
      {imageError && (
        <div className={`absolute inset-0 bg-gradient-to-br ${config.color} flex items-center justify-center`}>
          <span className="text-2xl">{guide === 'luna' ? '👩' : guide === 'enzo' ? '👨' : guide === 'pixie' ? '🧚' : guide === 'nyx' ? '🌙' : '🔮'}</span>
        </div>
      )}
      
      {/* Overlay de clignement */}
      <AnimatePresence>
        {isBlinking && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
          />
        )}
      </AnimatePresence>
      
      {/* Bordure gradient */}
      <div 
        className={`absolute inset-0 rounded-full border-2 border-transparent bg-gradient-to-br ${config.color} opacity-50`}
        style={{ 
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          padding: '2px',
        }}
      />
    </motion.div>
  );
};

/**
 * 🗨️ GuideBubble - Bulle de dialogue animée
 */
export const GuideBubble = ({
  guide = 'luna',
  message,
  isTyping = false,
  onComplete,
  isDarkMode = true,
  position = 'right', // 'left' | 'right'
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const config = GUIDE_CONFIG[guide] || GUIDE_CONFIG.luna;
  
  // 💬 Effet de frappe (typing effect)
  useEffect(() => {
    if (!message || !isTyping) {
      setDisplayedText(message || '');
      setIsComplete(true);
      return;
    }
    
    setDisplayedText('');
    setIsComplete(false);
    
    let index = 0;
    const typingSpeed = 30; // ms par caractère
    
    const interval = setInterval(() => {
      if (index < message.length) {
        setDisplayedText(prev => prev + message[index]);
        index++;
      } else {
        clearInterval(interval);
        setIsComplete(true);
        onComplete?.();
      }
    }, typingSpeed);
    
    return () => clearInterval(interval);
  }, [message, isTyping, onComplete]);
  
  const bubblePosition = position === 'left' 
    ? 'rounded-bl-none' 
    : 'rounded-br-none';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.9 }}
      className={`
        max-w-xs p-4 rounded-2xl ${bubblePosition}
        ${isDarkMode 
          ? 'bg-gray-800/90 backdrop-blur-lg border border-gray-700' 
          : 'bg-white/90 backdrop-blur-lg border border-gray-200 shadow-lg'}
      `}
    >
      <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
        {displayedText}
        {!isComplete && (
          <motion.span
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.8, repeat: Infinity }}
            className={`inline-block w-2 h-4 ml-1 ${isDarkMode ? 'bg-pink-400' : 'bg-pink-500'}`}
          />
        )}
      </p>
    </motion.div>
  );
};

/**
 * 🎪 GuidePresence - Système de présence intelligent (style macOS)
 * 
 * Gère l'apparition/disparition des guides selon le contexte
 */
export const GuidePresence = ({
  guide = 'luna',
  expression = 'welcome_smile',
  message,
  isVisible = true,
  position = 'bottom-right', // 'bottom-right' | 'bottom-left' | 'center'
  isDarkMode = true,
  onDismiss,
  onAction,
  actionLabel = "C'est parti !",
  showAction = true,
  minimizable = true,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const config = GUIDE_CONFIG[guide] || GUIDE_CONFIG.luna;
  
  const positionClasses = {
    'bottom-right': 'fixed bottom-6 right-6',
    'bottom-left': 'fixed bottom-6 left-6',
    'center': 'fixed bottom-1/4 left-1/2 -translate-x-1/2',
  };
  
  if (!isVisible) return null;
  
  // Version minimisée (juste l'avatar qui pulse)
  if (isMinimized) {
    return (
      <motion.div
        className={`${positionClasses[position]} z-50`}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
      >
        <div className="relative">
          <GuideAvatar
            guide={guide}
            expression={expression}
            size="md"
            onClick={() => setIsMinimized(false)}
          />
          {/* Badge notification */}
          <motion.div
            className="absolute -top-1 -right-1 w-4 h-4 bg-pink-500 rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </div>
      </motion.div>
    );
  }
  
  return (
    <motion.div
      className={`${positionClasses[position]} z-50 flex items-end gap-3`}
      initial={{ opacity: 0, y: 50, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.8 }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
    >
      {/* Avatar */}
      <GuideAvatar
        guide={guide}
        expression={expression}
        size="lg"
        glow={true}
      />
      
      {/* Carte de dialogue */}
      <motion.div
        className={`
          max-w-sm rounded-2xl overflow-hidden shadow-2xl
          ${isDarkMode 
            ? 'bg-gray-900/95 backdrop-blur-xl border border-gray-800' 
            : 'bg-white/95 backdrop-blur-xl border border-gray-200'}
        `}
        style={{
          boxShadow: `0 20px 60px ${config.bgGlow}`,
        }}
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${config.color} p-3 flex items-center justify-between`}>
          <div>
            <h4 className="font-bold text-white text-sm">{config.name}</h4>
            <p className="text-white/70 text-xs">{config.role}</p>
          </div>
          <div className="flex gap-1">
            {minimizable && (
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1.5 rounded-full hover:bg-white/20 text-white/80 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
            )}
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="p-1.5 rounded-full hover:bg-white/20 text-white/80 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {/* Message */}
        <div className="p-4">
          <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {message}
          </p>
        </div>
        
        {/* Action */}
        {showAction && onAction && (
          <div className="px-4 pb-4">
            <motion.button
              onClick={onAction}
              className={`w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r ${config.color} hover:opacity-90 transition-opacity`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {actionLabel} 🚀
            </motion.button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

/**
 * 📍 GuideIndicator - Petit indicateur de présence (coin de l'écran)
 * Pour une présence encore plus discrète
 */
export const GuideIndicator = ({
  guide = 'luna',
  isActive = false,
  unreadCount = 0,
  onClick,
}) => {
  const config = GUIDE_CONFIG[guide] || GUIDE_CONFIG.luna;
  const images = GUIDE_IMAGES[guide] || GUIDE_IMAGES.luna;
  
  return (
    <motion.button
      onClick={onClick}
      className="relative"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
    >
      <div 
        className={`w-10 h-10 rounded-full overflow-hidden border-2 ${isActive ? 'border-green-400' : 'border-gray-600'}`}
        style={{
          boxShadow: isActive ? `0 0 15px ${config.bgGlow}` : 'none',
        }}
      >
        {images.default ? (
          <img src={images.default} alt={config.name} className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${config.color} flex items-center justify-center`}>
            <span className="text-lg">{guide === 'luna' ? '👩' : '👨'}</span>
          </div>
        )}
      </div>
      
      {/* Badge de notification */}
      {unreadCount > 0 && (
        <motion.div
          className="absolute -top-1 -right-1 w-5 h-5 bg-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </motion.div>
      )}
      
      {/* Indicateur "en ligne" */}
      {isActive && (
        <motion.div
          className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-gray-900"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}
    </motion.button>
  );
};

export default GuideAvatar;
